import React, {
  ReactNode, useMemo, useCallback,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  ConversationType,
  selectCurrentSelectedChat,
  selectMetadataById,
  selectUserById,
  PrivateConversationMetadata,
  GroupConversationMetadata,
  ConversationTypeHandlerBuilder,
  MessageType,
  setChatAlert,
  UserReachabilityStatus,
} from 'models/twilio';
import { useTranslation } from 'react-i18next';
import { VideocamRounded } from '@material-ui/icons';
import metadataEquality from '../utils/metadataEquality';
import ColoredMaterialIcon from '../components/styled/ColoredMaterialIcon';
import { IMAGES, RONGCLOUD_CONVTYPES } from '../constants';
import GroupMemberButton from '../components/GroupMemberButton';
import ChatIconButton from '../components/styled/ChatIconButton';
import LeaveGroupButton from '../components/LeaveGroupButton';
import ChatNameCardIcon from '../components/assets/ChatNameCardIcon';
import useCurrentAttendee from './useCurrentAttendee';
import useTwilioMessageSend from './useTwilioMessageSend';
import ChatTitleButtonGroupContainer from '../components/styled/ChatTitleButtonGroup';
import useFirebaseChatConfig from './useFirebaseChatConfig';
import usePublicChatroomName from './usePublicChatroomName';
import useTwilioVideoCall from './useTwilioVideoCall';
import throttleFn from '../utils/throttleFn';
import TRANSLATIONS from '../translations';

interface ChatTitleData {
  chatName: string;
  userTag: string;
  userDescription: string;
  iconSrc?: string;
  reachabilityStatus?: UserReachabilityStatus;
}

interface ChatTitleButtonGroup {
  buttonGroup: () => ReactNode;
}

type ConversationTypeHandler = ChatTitleData
  & ChatTitleButtonGroup;

const useTwilioTitle = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    name,
    email,
    userDescription,
    uuid,
    firstName,
    lastName,
  } = useCurrentAttendee();
  const { sendMessage } = useTwilioMessageSend();
  const { createVideoCall } = useTwilioVideoCall();
  const {
    boothChatroomDisabled, groupMemberList, contactCard, privateVideoCall,
  } = useFirebaseChatConfig();
  const currentSelectedChat = useSelector(selectCurrentSelectedChat);
  const metadata = useSelector(selectMetadataById(currentSelectedChat), metadataEquality({
    otherUserId: (prev, next) => shallowEqual(prev, next),
    conversationName: (prev, next) => shallowEqual(prev, next),
    uniqueName: (prev, next) => shallowEqual(prev, next),
    id: (prev, next) => shallowEqual(prev, next),
  }));
  const publicChatroomName = usePublicChatroomName(metadata.uniqueName);
  const otherUser = useSelector(selectUserById((metadata as PrivateConversationMetadata).otherUserId));

  const initPrivateVideoCall = useCallback(() => {
    if (!otherUser || !metadata) return;
    createVideoCall(
      otherUser?.name || metadata.id,
      metadata.id,
      otherUser?.id,
      RONGCLOUD_CONVTYPES.PRIVATE,
    );
  }, [metadata, otherUser, createVideoCall]);

  const maxContactCardAlert = useCallback(() => {
    dispatch(setChatAlert({ message: TRANSLATIONS.COMMON.CONTACT_CARD_THROTTLE[1], translationKey: TRANSLATIONS.COMMON.CONTACT_CARD_THROTTLE[0], severity: 'info' }));
  }, [dispatch]);

  const shareContactCard = useCallback(throttleFn(
    async () => {
      const contactInfo = {
        firstName,
        lastName,
        name,
        email,
        description: userDescription,
        contactId: uuid,
      };
      await sendMessage('[Contact Card]', { messageType: MessageType.ContactCard, contact: contactInfo });
    },
    5000,
    maxContactCardAlert,
  ), [
    sendMessage,
    name,
    email,
    userDescription,
    uuid,
    maxContactCardAlert,
  ]);

  const titleData = useMemo((): ConversationTypeHandlerBuilder<ChatTitleData> => ({
    [ConversationType.PRIVATE]: {
      chatName: otherUser?.name || currentSelectedChat,
      userTag: otherUser?.tag || null,
      userDescription: otherUser?.description || null,
      reachabilityStatus: otherUser?.reachabilityStatus ?? UserReachabilityStatus.OFFLINE,
    },
    [ConversationType.GROUP]: {
      chatName: (metadata as GroupConversationMetadata).conversationName || currentSelectedChat,
      userTag: null,
      userDescription: null,
      iconSrc: IMAGES.GROUP_CHAT,
    },
    [ConversationType.ANNOUNCEMENT]: {
      chatName: t(...TRANSLATIONS.JOINED_ROOMS.ANNOUNCEMENT),
      userTag: null,
      userDescription: null,
      iconSrc: IMAGES.ANNOUNCEMENT,
    },
    [ConversationType.PUBLIC]: {
      chatName: publicChatroomName,
      userTag: null,
      userDescription: null,
      iconSrc: IMAGES.GROUP_CHAT,
    },
    [ConversationType.UNKNOWN]: {
      chatName: currentSelectedChat,
      userTag: null,
      userDescription: null,
    },
  }), [currentSelectedChat, metadata, otherUser, publicChatroomName, t]);

  const buttonGroup = useMemo((): ConversationTypeHandlerBuilder<ChatTitleButtonGroup> => ({
    [ConversationType.PRIVATE]: {
      buttonGroup: () => (
        <ChatTitleButtonGroupContainer key={`PrivateButtonGroup-${currentSelectedChat}`}>
          {privateVideoCall && (
            <ChatIconButton id="chat-video-call-button" onClick={initPrivateVideoCall}>
              <ColoredMaterialIcon Icon={VideocamRounded} />
            </ChatIconButton>
          )}
          {contactCard && (
            <ChatIconButton id="chat-send-name-card-button" onClick={shareContactCard}>
              <ColoredMaterialIcon Icon={ChatNameCardIcon} />
            </ChatIconButton>
          )}
        </ChatTitleButtonGroupContainer>
      ),
    },
    [ConversationType.GROUP]: {
      buttonGroup: () => (
        <ChatTitleButtonGroupContainer key={`GroupChatButtonGroup-${currentSelectedChat}`}>
          <LeaveGroupButton conversationSid={currentSelectedChat} />
          {(!boothChatroomDisabled && groupMemberList) && <GroupMemberButton conversationSid={currentSelectedChat} />}
        </ChatTitleButtonGroupContainer>
      ),
    },
    [ConversationType.PUBLIC]: {
      buttonGroup: () => (
        <ChatTitleButtonGroupContainer key={`PublicChatroomChatButtonGroup-${currentSelectedChat}`}>
          <LeaveGroupButton conversationSid={currentSelectedChat} isPublicChatroom />
          {groupMemberList && <GroupMemberButton conversationSid={currentSelectedChat} />}
        </ChatTitleButtonGroupContainer>
      ),
    },
    [ConversationType.ANNOUNCEMENT]: {
      buttonGroup: () => <ChatTitleButtonGroupContainer key={`AnnouncementChatButtonGroup-${currentSelectedChat}`} />,
    },
    [ConversationType.UNKNOWN]: {
      buttonGroup: () => null,
    },
  }), [currentSelectedChat, shareContactCard, boothChatroomDisabled, groupMemberList, contactCard, privateVideoCall, initPrivateVideoCall]);

  return useMemo((): ConversationTypeHandler => ({
    ...titleData[metadata?.conversationType || ConversationType.UNKNOWN],
    ...buttonGroup[metadata?.conversationType || ConversationType.UNKNOWN],
  }), [titleData, buttonGroup, metadata]);
};

export default useTwilioTitle;
